import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { isEmpty } from 'lodash';
import { LicensesPageService, UserService } from '@nrwl/common-lib';
import { licenseData } from './licenseData';

export interface ILicenseDataDetail {
  featureName: string;
  featureTitle: string;
  version: string;
  hash: string;
  notValidBefore: string;
  expirationDate: string;
  count: number;
  used: number;
  showUsedCount: boolean;
  description: string;
}

@Component({
  selector: 'nrwl-licenses-page',
  templateUrl: './licenses.page.template.html',
  styleUrls: ['./licenses.page.style.scss'],
  animations: [
    trigger('inAnimation', [
      transition(':enter', [
        style({
          height: 0,
          opacity: 0
        }),
        animate('0.6s ease-out', style({ height: 300, opacity: 1 }))
      ])
    ])
  ]
})
export class LicensesPageComponent implements OnInit, OnDestroy {
  @ViewChild('file') file: ElementRef<HTMLInputElement>;
  public files: Set<File> = new Set();
  isLoading = true;
  uploading = false;

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  progress: any;

  showClearButton = false;
  fileName = '';
  lockCode = '';
  versionNumber = '14';
  showLicenseError = false;
  errorRetrievalMsg = '';
  showLicenseAvailable = true;
  lockCodeObtained = 0;
  errorForLicenseFile = [];

  activeLicenses: Array<ILicenseDataDetail> = [];
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(private licenseService: LicensesPageService, private userService: UserService) {}

  ngOnInit() {
    this.orderLockCode();
    this.refreshLicenseCount();
    this.getLicenseVersion();
    if (this.userService.isFusionUIProject) this.versionNumber = '14';
  }

  ngOnDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe.next();
      this.unsubscribe.complete();
    }
  }

  getLicenseVersion(): void {
    this.licenseService
      .getLicenseVersionValue$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        this.versionNumber = response;
      });
  }

  orderLockCode(): void {
    this.licenseService
      .getLockCode()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        value => {
          if (value && value.lockCode) {
            this.lockCode = value.lockCode;
            this.lockCodeObtained = 2;
          }
        },
        () => {
          this.lockCodeObtained = 1;
        }
      );
  }

  refreshLicenseCount(): void {
    this.activeLicenses = [];
    this.isLoading = true;
    this.orderLicenseCount();
    this.clearSelectedFile();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  orderLicenseCount(): void {
    this.showLicenseError = false;
    this.showLicenseAvailable = true;
    this.licenseService
      .getLicenseCount()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        value => {
          if (value && value.length > 0) {
            this.licenseService.setLicenseData(value);
            this.updatedWithDesc(value);
          } else {
            this.showLicenseAvailable = false;
            this.errorRetrievalMsg = 'There are no licenses installed';
          }
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
          this.showLicenseError = true;
          this.errorRetrievalMsg = 'Could not retrieve license information';
        }
      );
  }

  updatedWithDesc(data: Array<ILicenseDataDetail>): void {
    data.map(item => {
      const monitorLicense = licenseData.find(license => license.featureName === item.featureName);
      item.featureTitle = monitorLicense ? monitorLicense.displayName : item.featureName;
      item.description = monitorLicense ? monitorLicense.description : '';
      item.showUsedCount = this.licenseShowCount(item.featureName);
    });
    const gigatestUser = this.userService.isGigatestUser;
    data = data.filter(l => {
      if (gigatestUser) {
        return l.featureName !== 'ACT-NUM-TESTS' && l.featureName !== 'ACT-KAFKA-STREAMING-DB';
      } else {
        return true;
      }
    });
    this.activeLicenses = data.sort((a, b) => (a.featureTitle > b.featureTitle ? 1 : -1));
  }

  licenseShowCount(name: string): boolean {
    switch (name) {
      case 'EAX-NUM-TESTS':
      case 'EAX-MB-AGGR-BW':
      case 'EAX-NUM-FLOWS':
      case 'EAX-NUM-USERS':
      case 'EAX-REG-VTA':
      case 'EAX-REG-VPMA':
      case 'EAX-REG-TBMTS5800-V2':
      case 'EAX-REG-TBMTS5800-100G':
      case 'EAX-REG-QT600-10G':
      case 'EAX-REG-VPMA-L':
      case 'EAX-REG-NSC':
      case 'EAX-REG-JMEP-10G':
      case 'EAX-REG-JMEP-1G':
      case 'EAX-REG-ESAM':
      case 'EAX-REG-1PT':
        return true;
      default:
        return false;
    }
  }

  onFilesAdded(): void {
    const files: FileList = this.file.nativeElement.files;
    const tempFileNameList = [];

    for (let i = 0; i < files.length; ++i) {
      this.files.add(files[i]);
      tempFileNameList.push(files[i].name);
    }

    this.fileName = tempFileNameList.join(', ');
    this.showClearButton = true;
  }

  clearSelectedFile(): void {
    this.errorForLicenseFile.length = 0;
    this.files.clear();
    if (this.file && this.file.nativeElement) {
      this.file.nativeElement.value = '';
    }
    this.fileName = '';
    this.showClearButton = false;
  }

  saveLicenseFile(): void {
    this.uploading = true;
    this.errorForLicenseFile = [];
    // upload should be disabled if no lock code available
    this.progress = this.licenseService.uploadLicenseFile(this.files);
    const allProgressObservables = [];

    for (const key in this.progress) {
      allProgressObservables.push(this.progress[key].progress);
    }

    forkJoin(allProgressObservables).subscribe(() => {
      this.uploading = false;

      for (const key in this.progress) {
        if (!isEmpty(this.progress[key].error)) {
          this.errorForLicenseFile.push(this.progress[key].error);
        }
      }

      if (this.errorForLicenseFile.length === 0) {
        this.refreshLicenseCount();
      }
    });
  }

  hasErrors(): boolean {
    return this.errorForLicenseFile.length > 0;
  }
}
